import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LogIn,
  ChevronDown,
  Check,
  Menu,
  X,
  ArrowUpRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, isLanguageCode, aranorSiteUrl } from '../i18n/languages';
import {
  LocalLink,
  LocalNavLink,
  useLang,
} from '../i18n/useLocalizedNav';
import { SOCIALS } from '../content/socials';

// Year is injected as a compile-time constant by Vite (see
// vite.config.ts `define`). Same value in the prerender bundle and
// the client bundle → no hydration mismatch when the calendar year
// ticks over between build and visit.
declare const __BUILD_YEAR__: number;
const BUILD_YEAR = __BUILD_YEAR__;

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `font-heading font-bold transition-colors ${
    isActive
      ? 'text-warm-800 border-b-2 border-brand-primary pb-1'
      : 'text-warm-600 hover:text-warm-800'
  }`;

export const LanguageDropdown: React.FC = () => {
  const { i18n } = useTranslation();
  const lang = useLang();
  const switchLang = useSwitchLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current =
    LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const switchTo = (code: string) => {
    switchLang(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 px-3 py-1.5 text-xs font-heading font-bold text-warm-700 bg-warm-100 rounded-lg hover:bg-warm-200 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {current.label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul
          className="absolute right-0 mt-2 w-28 bg-white border border-warm-200 rounded-xl shadow-lg py-1 z-20"
          role="listbox"
        >
          {LANGUAGES.map((lng) => (
            <li key={lng.code}>
              <button
                onClick={() => switchTo(lng.code)}
                className={`w-full flex items-center justify-between px-3 py-2 text-xs font-heading font-bold transition-colors ${
                  lang === lng.code
                    ? 'text-warm-800 bg-warm-100'
                    : 'text-warm-600 hover:bg-warm-100'
                }`}
                role="option"
                aria-selected={lang === lng.code}
              >
                {lng.label}
                {lang === lng.code && (
                  <Check className="w-3.5 h-3.5 text-brand-accent" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Hook: switch language by navigating to the same page under the new
// /:lang prefix. Shared by the desktop dropdown and the mobile panel so
// the URL stays the single source of truth.
const useSwitchLang = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (code: string) => {
    const parts = location.pathname.split('/');
    if (isLanguageCode(parts[1])) {
      parts[1] = code;
    } else {
      parts.splice(1, 0, code);
    }
    navigate(parts.join('/') + location.search, { replace: false });
  };
};

// Vertical language list for the mobile slide-in panel — same behaviour as
// LanguageDropdown but laid out as a grid of pills instead of a popover.
const LanguagePicker: React.FC<{ onPick?: () => void }> = ({ onPick }) => {
  const lang = useLang();
  const switchTo = useSwitchLang();
  return (
    <div className="grid grid-cols-3 gap-2">
      {LANGUAGES.map((lng) => {
        const active = lang === lng.code;
        return (
          <button
            key={lng.code}
            onClick={() => {
              switchTo(lng.code);
              onPick?.();
            }}
            className={`flex items-center justify-center gap-1 px-2 py-2.5 rounded-lg text-xs font-heading font-bold transition-colors ${
              active
                ? 'bg-brand-primary text-brand-secondary'
                : 'bg-warm-100 text-warm-700 hover:bg-warm-200'
            }`}
            aria-pressed={active}
          >
            {lng.label}
            {active && <Check className="w-3.5 h-3.5" />}
          </button>
        );
      })}
    </div>
  );
};

const Layout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const lang = useLang();
  // Pinned at module level (see top of file) so prerender and client
  // render the same number — `new Date()` here would cause a hydration
  // mismatch once the year ticks over after a build.
  const year = BUILD_YEAR;
  const aranorUrl = aranorSiteUrl(lang);

  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  // Lock body scroll + close on Escape while the mobile menu is open.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const initials = (user?.fullName || user?.email || 'A')
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-warm-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-6">
          {/* Brand */}
          <LocalLink to="/" className="flex items-center gap-2.5 shrink-0">
            <img
              src="/images/logo.png"
              alt="Aranor Passport logo"
              className="w-9 h-9 object-contain"
              width={36}
              height={36}
            />
            <div className="leading-none">
              <span className="block font-display text-xl text-warm-800">
                Aranor
              </span>
              <span className="block font-heading font-bold text-[9px] tracking-[0.25em] text-warm-500">
                PASSPORT
              </span>
            </div>
          </LocalLink>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 flex-1 justify-center">
            <LocalNavLink to="/" end className={navLinkClass}>
              {t('nav_home')}
            </LocalNavLink>
            <LocalNavLink to="/locations" className={navLinkClass}>
              {t('locations')}
            </LocalNavLink>
            <LocalNavLink to="/my-stamps" className={navLinkClass}>
              {t('my_stamps')}
            </LocalNavLink>
            <a
              href={aranorUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-heading font-bold text-warm-600 hover:text-warm-800 transition-colors"
            >
              {t('nav_about_aranor')}
            </a>
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-4 shrink-0">
            <div className="hidden sm:block">
              <LanguageDropdown />
            </div>

            {/* Desktop auth / profile (hidden on mobile — lives in the menu) */}
            {user ? (
              <LocalLink
                to="/profile"
                className="hidden md:flex items-center gap-3 pl-1 pr-1 sm:pr-3 py-1 rounded-full hover:bg-warm-100 transition-colors"
              >
                <div className="w-9 h-9 bg-brand-primary rounded-full flex items-center justify-center font-heading font-bold text-sm text-brand-secondary">
                  {initials}
                </div>
                <div className="hidden sm:block leading-none">
                  <span className="block text-sm font-heading font-bold text-warm-800">
                    {user.fullName || user.email}
                  </span>
                  <span className="block text-[10px] font-bold tracking-widest text-warm-500 uppercase">
                    {/* Tiered knowledge levels — 5 tiers, coming soon */}
                    {t('rank_explorer')}
                  </span>
                </div>
              </LocalLink>
            ) : (
              <div className="hidden md:flex items-center gap-4">
                <LocalLink
                  to="/login"
                  className="font-heading font-bold text-warm-700 hover:text-warm-800 transition-colors"
                >
                  {t('nav_sign_in')}
                </LocalLink>
                <LocalLink
                  to="/register"
                  className="flex items-center gap-2 px-4 py-2.5 bg-brand-primary text-brand-secondary rounded-xl font-heading font-bold hover:bg-brand-accent transition-all"
                >
                  {t('nav_claim_passport')}
                </LocalLink>
              </div>
            )}

            {/* Mobile burger */}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 -mr-1 rounded-lg text-warm-700 hover:bg-warm-100 transition-colors"
              aria-label={t('nav_open_menu')}
              aria-haspopup="dialog"
              aria-expanded={menuOpen}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-grow container mx-auto px-4 pt-8 pb-16">
        {children}
      </main>

      <footer className="bg-warm-800 text-warm-100 mt-auto">
        <div className="container mx-auto px-4 pt-12 lg:pb-12 pb-20">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
            {/* Brand block */}
            <div className="col-span-2 space-y-4">
              <div className="flex items-center gap-2.5">
                <img
                  src="/images/logo.png"
                  alt="Aranor Passport logo"
                  className="w-9 h-9 object-contain"
                  width={36}
                  height={36}
                />
                <span className="font-display text-2xl text-brand-primary">
                  Aranor
                </span>
              </div>
              <p className="font-display italic text-warm-100 text-lg leading-snug max-w-xs">
                {t('footer_tagline')}
                <br />
                {t('footer_slogan')}
              </p>
            </div>

            <FooterColumn
              title={t('footer_col_passport')}
              links={[
                { label: t('footer_my_stamps'), to: '/my-stamps' },
                { label: t('footer_available_stamps'), to: '/my-stamps' },
              ]}
            />
            <FooterColumn
              title={t('footer_col_explore')}
              links={[
                { label: t('footer_all_locations'), to: '/locations' },
                { label: t('category.food_drink'), to: '/locations?category=Food+%26+Drink' },
                { label: t('category.adventure'), to: '/locations?category=Adventure' },
                { label: t('category.sightseeing'), to: '/locations?category=Sightseeing' },
              ]}
            />
            <FooterColumn
              title={t('footer_col_aranor')}
              links={[
                { label: t('footer_about'), to: '/about' },
                { label: t('footer_aranor_travel'), href: aranorUrl },
              ]}
            />
            <FooterColumn
              title={t('footer_col_legal')}
              links={[
                { label: t('footer_terms'), to: '/terms' },
                { label: t('footer_privacy'), to: '/privacy' },
                { label: t('footer_contact'), to: '/contact' },
              ]}
            />
          </div>

          {/* "Follow us" row — sits between the footer columns and the
              copyright divider. Each link is an external profile, so all
              get target="_blank" and rel="noopener noreferrer". Icons are
              currentColor → Tailwind text utilities control the tint. */}
          <nav
            aria-label="Follow Aranor on social media"
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <span className="font-heading font-bold text-[11px] tracking-[0.2em] text-brand-primary uppercase">
              {t('footer_follow_us')}
            </span>
            <ul className="flex items-center gap-2">
              {SOCIALS.map(({ label, url, Icon }) => (
                <li key={label}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="inline-flex w-10 h-10 items-center justify-center rounded-lg text-warm-100/80 hover:text-brand-primary hover:bg-white/5 transition-colors"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="border-t border-warm-600/40 mt-6 pt-6 text-warm-300 text-xs">
            {t('footer_rights', { year })}
          </div>
        </div>
      </footer>

      {/* Mobile slide-in menu (burger). Backdrop + right-hand panel with all
          nav links, the language picker, and auth/profile actions stacked
          vertically — mirrors the desktop header. */}
      <div
        className={`md:hidden fixed inset-0 z-50 ${
          menuOpen ? '' : 'pointer-events-none'
        }`}
        aria-hidden={!menuOpen}
      >
        {/* Backdrop */}
        <div
          onClick={closeMenu}
          className={`absolute inset-0 bg-warm-800/40 transition-opacity duration-200 ${
            menuOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Panel */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('nav_menu')}
          className={`absolute top-0 right-0 h-full w-[82%] max-w-xs bg-white shadow-2xl flex flex-col transition-transform duration-200 ease-out ${
            menuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Panel header */}
          <div className="h-16 px-4 flex items-center justify-between border-b border-warm-200 shrink-0">
            <span className="font-heading font-bold text-[9px] tracking-[0.25em] text-warm-500 uppercase">
              {t('nav_menu')}
            </span>
            <button
              type="button"
              onClick={closeMenu}
              className="inline-flex items-center justify-center w-10 h-10 -mr-1 rounded-lg text-warm-700 hover:bg-warm-100 transition-colors"
              aria-label={t('nav_close_menu')}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div
            className="flex-1 overflow-y-auto px-4 py-5 flex flex-col gap-6"
            onClick={(e) => {
              // Any link tap inside the panel closes it. Anchors/NavLinks
              // bubble their click here; ignore non-link clicks.
              if ((e.target as HTMLElement).closest('a')) closeMenu();
            }}
          >
            {/* Profile / auth */}
            {user ? (
              <LocalLink
                to="/profile"
                className="flex items-center gap-3 p-2 -m-2 rounded-xl hover:bg-warm-100 transition-colors"
              >
                <div className="w-11 h-11 bg-brand-primary rounded-full flex items-center justify-center font-heading font-bold text-brand-secondary">
                  {initials}
                </div>
                <div className="leading-tight min-w-0">
                  <span className="block text-sm font-heading font-bold text-warm-800 truncate">
                    {user.fullName || user.email}
                  </span>
                  <span className="block text-[10px] font-bold tracking-widest text-warm-500 uppercase">
                    {t('nav_profile')}
                  </span>
                </div>
              </LocalLink>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <LocalLink
                  to="/login"
                  className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-warm-200 font-heading font-bold text-warm-700 hover:bg-warm-100 transition-colors"
                >
                  <LogIn className="w-4 h-4" />
                  {t('nav_sign_in')}
                </LocalLink>
                <LocalLink
                  to="/register"
                  className="flex items-center justify-center px-3 py-2.5 rounded-xl bg-brand-primary text-brand-secondary font-heading font-bold hover:bg-brand-accent transition-all"
                >
                  {t('nav_claim_passport')}
                </LocalLink>
              </div>
            )}

            {/* Primary nav */}
            <nav className="flex flex-col">
              <LocalNavLink to="/" end className={mobileMenuLinkClass}>
                {t('nav_home')}
              </LocalNavLink>
              <LocalNavLink to="/locations" className={mobileMenuLinkClass}>
                {t('locations')}
              </LocalNavLink>
              <LocalNavLink to="/my-stamps" className={mobileMenuLinkClass}>
                {t('my_stamps')}
              </LocalNavLink>
              <LocalNavLink to="/about" className={mobileMenuLinkClass}>
                {t('footer_about')}
              </LocalNavLink>
              <a
                href={aranorUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
                className="flex items-center justify-between py-3 border-b border-warm-100 font-heading font-bold text-warm-600 hover:text-warm-800 transition-colors"
              >
                {t('nav_about_aranor')}
                <ArrowUpRight className="w-4 h-4 opacity-60" />
              </a>
            </nav>

            {/* Language */}
            <div className="space-y-2">
              <p className="font-heading font-bold text-[10px] tracking-[0.2em] text-warm-500 uppercase">
                {t('nav_language')}
              </p>
              <LanguagePicker onPick={closeMenu} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mobileMenuLinkClass = ({ isActive }: { isActive: boolean }) =>
  `py-3 border-b border-warm-100 font-heading font-bold transition-colors ${
    isActive ? 'text-brand-accent' : 'text-warm-800 hover:text-brand-accent'
  }`;

type FooterLink = { label: string; to?: string; href?: string };

const FooterColumn: React.FC<{ title: string; links: FooterLink[] }> = ({
  title,
  links,
}) => (
  <div className="space-y-3">
    <p className="font-heading font-bold text-[11px] tracking-[0.2em] text-brand-primary uppercase">
      {title}
    </p>
    <ul className="space-y-2.5">
      {links.map((link) => (
        <li key={link.label}>
          {link.to ? (
            <LocalLink
              to={link.to}
              className="text-sm text-warm-100/80 hover:text-brand-primary transition-colors"
            >
              {link.label}
            </LocalLink>
          ) : (
            <a
              href={link.href}
              {...(link.href?.startsWith('http')
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
              className="text-sm text-warm-100/80 hover:text-brand-primary transition-colors"
            >
              {link.label}
            </a>
          )}
        </li>
      ))}
    </ul>
  </div>
);

export default Layout;
